/**
 * ApplicationPackDetailPage.js — /applications/:id
 *
 * Reads /api/application-packs/{id} (already hydrated with job, base_cv,
 * cv_variant and cover_letter summary objects) and renders read-only
 * context plus two trivially safe editors: pack_status (select) and
 * notes (textarea).
 *
 * No bundled export, no state-machine enforcement, no generation triggers.
 */

import { useEffect, useState, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  ArrowLeft, Briefcase, Mail, Package, Loader2, MapPin, Building2,
  ExternalLink, Save, Download, Send,
} from 'lucide-react';
import { apiFetch } from '@/lib/api';
import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';
import SendViaGmailDialog from '@/components/SendViaGmailDialog';
// RC 1.50 Pack Cleanup (operator-locked, 2026-02) — Pack Detail
// page collapsed to a single overview. PackLocalTabs (Documents,
// Fit & Interview) removed; PackPreviewPanel, PackFitPanel and
// InterviewIntelligenceSection no longer render here. Fit lives
// in the application workflow (workspace), not the saved pack.
//
// RC 1.51 follow-up — Export Simplification (operator-locked, 2026-02):
// PackCoverSheetPreviewModal + the More dropdown retired alongside
// the dual PDF/DOCX export buttons. There is now ONE primary export
// action ("Download Application Pack"), so no preview / dropdown
// menu is required.
import PackReceiptPanel from '@/components/PackReceiptPanel';
import PackReadinessStrip from '@/components/PackReadinessStrip';
import PackTrustCard from '@/components/PackTrustCard';
import { Input } from '@/components/ui/input';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const PACK_STATUS_BADGES = {
  draft:     { label: 'Draft',     className: 'bg-ndor-warning-light text-ndor-warning border-ndor-warning/20' },
  ready:     { label: 'Ready',     className: 'bg-ndor-info-light text-ndor-info border-ndor-info/20' },
  submitted: { label: 'Submitted', className: 'bg-ndor-success-light text-ndor-success border-ndor-success/20' },
  archived:  { label: 'Archived',  className: 'bg-ndor-surface-alt text-ndor-ink-muted border-ndor-edge' },
};

export default function ApplicationPackDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [pack, setPack] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleting] = useState(false);
  const [bundling, setBundling] = useState(false);

  // RC 1.41 Phase B — pack documents are immutable post-create.
  // The list-of-all-variants / list-of-all-letters state used by
  // the retired PackDocumentWorkspace is intentionally removed; the
  // only doc data we need now is what's already hydrated on the
  // pack itself (`pack.cv_variant`, `pack.cover_letter`,
  // `pack.base_cv`).

  // Gmail / Send state.
  const [gmailConnected, setGmailConnected] = useState(false);
  const [gmailEmail, setGmailEmail] = useState(null);
  const [sendOpen, setSendOpen] = useState(false);
  const [receipts, setReceipts] = useState([]);
  // RC 1.41 Phase B — `linkLetterOpen` retired alongside
  // LinkCoverLetterDialog. Cover-letter assignment happens inside
  // the wizard, before pack creation.

  // Editable slice — we only PATCH these three when the user clicks Save.
  // RC 1.41 Phase B — `pack_name` is the third operational field
  // (alongside pack_status + notes). Pack documents (cv_variant_id,
  // cover_letter_id, base_cv_id, job_id) are immutable.
  const [statusDraft, setStatusDraft] = useState('draft');
  const [notesDraft, setNotesDraft] = useState('');
  const [packNameDraft, setPackNameDraft] = useState('');

  // Fit result for the pack's job (drives the Fit card + Interview Intelligence).
  const [fitResult, setFitResult] = useState(null);

  // RC 1.50 Pack Cleanup (operator-locked, 2026-02) — local tab
  // state + URL-hash sync REMOVED. Pack Detail is a single
  // overview page. Tabs (Documents, Fit & Interview) collapsed.

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const p = await apiFetch(`/api/application-packs/${id}`);
      setPack(p);
      setStatusDraft(p.pack_status || 'draft');
      setNotesDraft(p.notes || '');
      setPackNameDraft(p.pack_name || '');
    } catch (err) {
      toast.error(err?.message || 'Failed to load pack.');
      navigate('/applications');
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => { load(); }, [load]);

  // RC 1.47.P0.1.7 (2026-05-03) — Pack Interview Prep MUST reflect
  // the pack-bound CV, not the latest job-wide row.
  //
  // Pre-fix this fetched `/api/jobs/{job_id}/best-fit`, which
  // returns the latest cached score across ANY CV the user has
  // analysed for that job. If the user re-ran Analyse Fit against
  // a Source CV after creating the pack against a Tailored
  // Variant, the Interview Prep panel would silently discuss
  // strengths from the wrong CV. Subtle drift, undermines
  // "one pack, one truth".
  //
  // Post-fix: read `pack.canonical_fit` directly. Slice A's
  // `_canonical_score_payload` already attaches the full
  // narrative under `reasoning` (incl. `interview_prep`,
  // `key_strengths`, `key_risks`, `score_explanation`,
  // `bounds_applied`, `overall_fit_score`,
  // `repositioning_advice`, `cv_sub_score`,
  // `cv_sub_score_provisional`). Zero extra fetches; the data
  // already arrives on `/api/application-packs/{id}`.
  //
  // Graceful degradation:
  //   • No canonical_fit (e.g. fit_status === 'missing') →
  //     fitResult stays null → InterviewIntelligenceSection
  //     renders its existing "Interview Intelligence will appear
  //     after a fit analysis has been completed" empty state.
  //   • Canonical fit present but no interview_prep persisted
  //     (legacy rows pre-RC 1.47 Phase 2) → reasoning still
  //     carries strengths/risks; the section's own internal
  //     gating handles the rest.
  useEffect(() => {
    if (!pack) { setFitResult(null); return; }
    const cf = pack.canonical_fit;
    if (!cf) { setFitResult(null); return; }
    setFitResult({
      fit_score: cf.fit_score,
      reasoning_json: cf.reasoning || {},
    });
  }, [pack]);

  // RC 1.41 Phase B — the bulk variants/letters prefetch was used
  // exclusively by the retired PackDocumentWorkspace's swap UI. With
  // pack documents now immutable post-create, only the hydrated
  // pack row's `cv_variant` / `cover_letter` / `base_cv` summary
  // objects are needed; the modal preview lazy-fetches the body.

  // Inline swap retired in RC 1.41 Phase B — pack documents are
  // immutable. To change documents, start a new pack from /jobs/:id.

  // Load Gmail connection status + receipts for this pack.
  const loadGmail = useCallback(async () => {
    try {
      const s = await apiFetch('/api/gmail/status');
      setGmailConnected(!!s.connected);
      setGmailEmail(s.email || null);
    } catch { /* silent */ }
  }, []);

  const loadReceipts = useCallback(async () => {
    try {
      const r = await apiFetch(`/api/application-packs/${id}/send-receipts`);
      setReceipts(Array.isArray(r) ? r : []);
    } catch { /* silent */ }
  }, [id]);

  useEffect(() => { loadGmail(); loadReceipts(); }, [loadGmail, loadReceipts]);

  // Listen for the OAuth popup to postMessage us back, then finalise.
  useEffect(() => {
    async function handler(ev) {
      if (!ev?.data || ev.data.source !== 'ndorflow-gmail') return;
      if (ev.data.status !== 'ok' || !ev.data.payload) return;
      try {
        await apiFetch('/api/gmail/oauth/finalize', {
          method: 'POST', body: ev.data.payload,
        });
        toast.success(`Gmail connected as ${ev.data.payload.email_address}.`);
        loadGmail();
      } catch (err) {
        toast.error(err?.message || 'Gmail finalize failed.');
      }
    }
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, [loadGmail]);

  const connectGmail = async () => {
    try {
      const { auth_url } = await apiFetch('/api/gmail/oauth/start');
      window.open(auth_url, 'ndorflow-gmail-oauth', 'width=540,height=680');
    } catch (err) {
      toast.error(err?.message || 'Could not start Gmail OAuth.');
    }
  };

  const openSendDialog = () => {
    if (!gmailConnected) {
      connectGmail();
      return;
    }
    setSendOpen(true);
  };

  // RC 1.52 Phase 3A.DUP — Pack names are SYSTEM-generated and
  // collision-proof. `pack_name` is no longer user-editable here;
  // the dirty/save path now covers only status + notes. The
  // `packNameDraft` state is retained so the read-only Input
  // displays the persisted name without requiring extra plumbing.
  const dirty = pack && (
    statusDraft !== (pack.pack_status || 'draft') ||
    (notesDraft || '') !== (pack.notes || '')
  );

  const handleSave = async () => {
    if (!dirty) return;
    setSaving(true);
    try {
      const body = {};
      if (statusDraft !== pack.pack_status) body.pack_status = statusDraft;
      if ((notesDraft || '') !== (pack.notes || '')) body.notes = notesDraft;
      await apiFetch(`/api/application-packs/${id}`, { method: 'PATCH', body });
      toast.success('Pack updated.');
      await load();
    } catch (err) {
      toast.error(err?.message || 'Failed to update pack.');
    } finally {
      setSaving(false);
    }
  };

  // RC 1.20.POLISH (2026-05-07) — top-level in-pack Delete removed
  // (the Applications list page is the canonical delete surface).
  // The handler + `deleting` state were dropped along with the
  // button; export-button disabled checks now only gate on
  // `bundling || saving`.

  // RC 1.51 follow-up — Export Simplification (operator-locked,
  // 2026-02). One primary export action; one fixed-format ZIP. The
  // backend bundle always carries Tailored CV + Cover Letter as
  // DOCX (editable application assets) and Cover Sheet + Interview
  // Intelligence as PDF (governance artefacts) — when each is
  // present in the pack composition. Source CV is never exported.
  const handleExportBundle = async () => {
    setBundling(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) throw new Error('Not authenticated.');

      const res = await fetch(
        `${BACKEND_URL}/api/application-packs/${id}/export-bundle`,
        { headers: { Authorization: `Bearer ${session.access_token}` } },
      );
      if (!res.ok) {
        let msg = `Export failed (${res.status})`;
        try { const j = await res.json(); msg = j.detail || msg; } catch { /* binary */ }
        throw new Error(msg);
      }

      const cd = res.headers.get('Content-Disposition') || '';
      const m = cd.match(/filename="([^"]+)"/);
      const filename = m ? m[1] : 'ApplicationPack.zip';

      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success('Bundle downloaded.');
    } catch (err) {
      toast.error(err?.message || 'Bundle export failed.');
    } finally {
      setBundling(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="h-6 w-6 animate-spin text-ndor-ink-muted" />
      </div>
    );
  }

  if (!pack) return null;

  const { job, base_cv: baseCv, cv_variant: variant, cover_letter: letter } = pack;
  const badge = PACK_STATUS_BADGES[pack.pack_status] || PACK_STATUS_BADGES.draft;

  // RC 1.49.x EXPORT_RULES (operator-locked, 2026-05-06)
  // RC 1.51 Pack Separation (operator-locked, 2026-02):
  // Interview Prep is a standalone generated artefact. The pack is
  // exportable when AT LEAST ONE generated artefact exists:
  //   • Tailored CV (`variant`)
  //   • Cover Letter (`letter`)
  //   • Interview Prep (`pack_snapshot.interview_prep` top-level,
  //     legacy fallback to `pack_snapshot.fit.reasoning_json
  //     .interview_prep`)
  // Source CV remains private/reference-only — NEVER exported.
  // Interview Intelligence PDF + Cover Sheet are produced whenever
  // `has_interview_prep` is true (NOT gated on Tailored CV any more).
  const hasTailoredCv     = !!variant;
  const hasCoverLetter    = !!letter;
  const _packSnap = pack?.pack_snapshot || {};
  const _ipTopLevel = _packSnap?.interview_prep;
  const _ipLegacy = _packSnap?.fit?.reasoning_json?.interview_prep;
  const hasInterviewPrep  = !!(
    (_ipTopLevel && typeof _ipTopLevel === 'object'
      && Object.keys(_ipTopLevel).length > 0)
    || (_ipLegacy && typeof _ipLegacy === 'object'
      && Object.keys(_ipLegacy).length > 0)
  );
  const hasIntelligence   = hasInterviewPrep; // Interview Intel + Cover Sheet
  const canExport         = hasTailoredCv || hasCoverLetter || hasInterviewPrep;

  // Dynamic label per the locked matrix.
  // RC 1.51 follow-up — Export Simplification (operator-locked,
  // 2026-02). Single export button with one label. The download
  // itself is the source of truth — the prior "This export
  // includes: …" helper line was retired (operator decision,
  // 2026-02-10) because pack readiness chips already convey
  // high-level state and the helper occasionally mis-stated the
  // ZIP contents.
  const exportLabel = 'Download Application Pack';
  const exportEnabledTitle = canExport
    ? 'Download the application pack ZIP.'
    : 'This pack has no generated artefacts yet. Generate a Tailored CV, Cover Letter, or Interview Prep to create an exportable pack.';

  // RC 1.50 Pack Cleanup (2026-02) — Pack Detail is now a single
  // overview surface. Document preview and Fit & Interview tabs
  // were removed; per-tab counts no longer needed.

  // Backend tells us which statuses this pack may move to next (always
  // includes the current one). Fall back to just the current status if
  // the backend is an older build that doesn't emit the field.
  const STATUS_LABELS = { draft: 'Draft', ready: 'Ready', submitted: 'Submitted', archived: 'Archived' };
  const allowedTransitions = (
    pack.allowed_status_transitions && pack.allowed_status_transitions.length > 0
      ? pack.allowed_status_transitions
      : [pack.pack_status || 'draft']
  );

  return (
    <div data-testid="application-pack-detail-page" className="space-y-5 max-w-4xl">
      {/* Header — RC 1.20.POLISH (operator-locked, 2026-05-07).
          Title row uses outer `flex-wrap` so the export buttons
          wrap to a second row on narrow viewports rather than
          squeezing the title. The title <span> uses
          `whitespace-nowrap` (NO `truncate`) so "Application pack"
          never stacks into a two-line block AND never visibly
          crops. Font scaled down a half-step on small viewports
          (text-xl → text-2xl at sm) so the title comfortably fits
          the iPhone-12-mini-class viewport (375px). */}
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <Link to="/applications" className="text-xs text-ndor-ink-muted hover:text-ndor-ink inline-flex items-center gap-1 mb-2">
            <ArrowLeft className="h-3 w-3" /> Back to applications
          </Link>
          <h2 className="text-xl sm:text-2xl font-semibold tracking-tight text-ndor-ink font-outfit flex items-center gap-2">
            <Package className="h-5 w-5 text-ndor-ink-muted shrink-0" />
            <span className="whitespace-nowrap">Application pack</span>
            <Badge
              variant="outline"
              className={`text-xs shrink-0 ${badge.className}`}
              data-testid="pack-status-badge"
            >
              {badge.label}
            </Badge>
          </h2>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {/* RC 1.51 follow-up — Export Simplification (operator-
              locked, 2026-02). Single primary export button. The
              ZIP is a fixed-format bundle (DOCX for editable assets,
              PDF for governance artefacts) — there is no longer
              a user-selectable format axis. Send via Gmail remains
              as a sibling action and uses the same fixed bundle. */}
          <Button
            size="sm"
            onClick={handleExportBundle}
            disabled={bundling || saving || deleting || !canExport}
            data-testid="pack-export-bundle-button"
            title={exportEnabledTitle}
            className="bg-ndor-gold text-ndor-ink hover:bg-ndor-gold/90 font-medium"
          >
            {bundling ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Download className="h-4 w-4 mr-1.5" />}
            {exportLabel}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={openSendDialog}
            disabled={bundling || saving || deleting || (!variant && !letter)}
            data-testid="pack-send-gmail-trigger"
            title={
              gmailConnected
                ? `Send via Gmail (${gmailEmail || 'connected'})`
                : 'Connect Gmail to send'
            }
          >
            <Send className="h-4 w-4 mr-1.5" />
            {gmailConnected
              ? `Send via Gmail (${gmailEmail || 'connected'})`
              : 'Connect Gmail to send'}
          </Button>
        </div>
      </div>

      {/* RC 1.51 follow-up — Export Helper Retired (operator-locked,
          2026-02-10). The contents-enumeration line was removed:
          the download itself is the source of truth, the pack
          readiness chips already convey high-level state, and the
          helper occasionally mis-stated the ZIP contents. The
          baseline-only diagnostic notice is preserved so users
          who reach the page with no generated artefacts still get
          a clear next-step nudge.

          Source CV remains private/reference-only and is NEVER
          included in any export — that contract is enforced
          server-side in `_build_pack_bundle_bytes` (it never reads
          base_cv body fields), independent of any UI copy. */}
      {!canExport && (
        <p
          className="text-xs text-ndor-ink-muted -mt-2"
          data-testid="pack-export-baseline-only-notice"
        >
          This pack has no generated artefacts yet. Generate a Tailored CV, Cover Letter, or Interview Prep to create an exportable pack.
        </p>
      )}

      {/* RC 1.50 Pack Cleanup (operator-locked, 2026-02) — single
          overview surface; PackLocalTabs removed. */}

      {/* Overview */}
      <div className="space-y-5" data-testid="pack-tab-overview">
          {/* RC 1.48.4C — Pack readiness strip at the top of the tab. */}
          <PackReadinessStrip pack={pack} />
          {/* Job */}
          <Card className="border-ndor-edge shadow-sm">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-ndor-ink flex items-center gap-2">
                <Briefcase className="h-4 w-4 text-ndor-ink-muted" /> Job
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {job ? (
                <>
                  <div className="flex items-center gap-2 flex-wrap">
                    <Link to={`/jobs/${job.id}`} className="text-base font-medium text-ndor-ink hover:underline" data-testid="pack-job-link">
                      {job.title}
                    </Link>
                    {job.url && (
                      <a href={job.url} target="_blank" rel="noopener noreferrer" className="text-xs text-ndor-ink-muted hover:text-ndor-ink inline-flex items-center gap-1">
                        <ExternalLink className="h-3 w-3" /> source
                      </a>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-ndor-ink-muted">
                    {job.company && <span className="inline-flex items-center gap-1"><Building2 className="h-3 w-3" />{job.company}</span>}
                    {job.location && <span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3" />{job.location}</span>}
                  </div>
                </>
              ) : (
                <p className="text-xs text-ndor-ink-muted">Job row unavailable.</p>
              )}
            </CardContent>
          </Card>

          {/* Status + notes (editable) */}
          <Card className="border-ndor-edge shadow-sm">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-ndor-ink">Pack details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-1.5">
                <Label className="text-xs text-ndor-ink-muted">Pack name</Label>
                {/* RC 1.52 Phase 3A.DUP — system-generated; not user-editable.
                    Users may delete the pack or rename the downloaded file
                    locally after export. */}
                <Input
                  value={packNameDraft}
                  readOnly
                  data-testid="pack-name-input"
                />
                <p className="text-[11px] text-ndor-ink-muted" data-testid="pack-name-hint">
                  System-generated. You can rename your downloaded file locally.
                </p>
              </div>
              <div className="space-y-1.5 max-w-xs">
                <Label className="text-xs text-ndor-ink-muted">Status</Label>
                <Select value={statusDraft} onValueChange={setStatusDraft}>
                  <SelectTrigger data-testid="pack-status-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {allowedTransitions.map((s) => (
                      <SelectItem key={s} value={s} data-testid={`pack-status-option-${s}`}>
                        {STATUS_LABELS[s] || s}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {pack.pack_status === 'archived' ? (
                  <p className="text-[11px] text-ndor-ink-muted">Archived packs are final. Delete if you no longer need it.</p>
                ) : allowedTransitions.length <= 1 ? null : (
                  <p className="text-[11px] text-ndor-ink-muted">
                    Moves forward only: draft → ready → submitted → archived (or archive a draft).
                  </p>
                )}
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs text-ndor-ink-muted">Notes</Label>
                <Textarea
                  value={notesDraft}
                  onChange={(e) => setNotesDraft(e.target.value)}
                  placeholder="e.g. referred by Jane, follow up in 2 weeks"
                  rows={3}
                  data-testid="pack-notes-input"
                />
              </div>
              <div className="flex justify-end">
                <Button
                  onClick={handleSave}
                  disabled={!dirty || saving}
                  data-testid="pack-save-button"
                >
                  {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Save className="h-4 w-4 mr-1.5" />}
                  Save changes
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* RC 1.47.P1.5 — Receipt & provenance card. Reads
              already-persisted data from the pack response (binding,
              canonical_fit, cv_variant.optimisation_artifacts.analytics_metadata,
              current_version). Pure read surface, collapsed by
              default. Provenance "Prep based on:" line stays visible
              even when collapsed. */}
          <PackReceiptPanel pack={pack} />

          {/* RC 1.48.4C — Trust attestation card, mirroring the
              exported PDF Keywords field (Pack run ID, canonical
              run ID, governance matrix version, generated-at). */}
          <PackTrustCard
            trustAnchors={{
              pack_run_id:         pack?.cv_variant_id || null,
              canonical_run_id:    pack?.canonical_fit?.analyse_run_id
                                || pack?.canonical_fit?.run_id
                                || null,
              matrix_version:      'matrix_v1_opportunity_preserving',
              generated_at:        pack?.updated_at || pack?.created_at || null,
              pack_version:        `RC 1.48.4B · pack ${(pack?.id || '').slice(0, 8)}`,
              governance_decision: pack?.canonical_fit?.governance_decision
                                || pack?.canonical_fit?.final_decision
                                || null,
            }}
          />

          {/* Send receipts */}
          {receipts.length > 0 && (
            <Card className="border-ndor-edge shadow-sm" data-testid="pack-send-receipts">
              <CardHeader><CardTitle className="text-base font-medium text-ndor-ink flex items-center gap-2"><Mail className="h-4 w-4" /> Gmail drafts</CardTitle></CardHeader>
              <CardContent className="space-y-2">
                {receipts.map((r) => (
                  <div
                    key={r.id}
                    className="flex items-center justify-between gap-3 text-sm border-b border-ndor-edge last:border-0 pb-2 last:pb-0"
                    data-testid="pack-send-receipt-row"
                  >
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-ndor-ink">
                        <span className="font-medium">{r.subject}</span>
                        <span className="text-ndor-ink-muted"> → {r.recipient_email}</span>
                      </div>
                      <div className="text-[11px] text-ndor-ink-muted">
                        {new Date(r.created_at).toLocaleString()} · {r.attachment_format.toUpperCase()} ·{' '}
                        {r.status === 'draft_created' ? 'Draft staged' :
                         r.status === 'draft_create_failed' ? `Failed: ${r.error_message || 'see logs'}` :
                         r.status}
                      </div>
                    </div>
                    {r.gmail_draft_url && (
                      <a href={r.gmail_draft_url} target="_blank" rel="noopener noreferrer">
                        <Button variant="outline" size="sm"><ExternalLink className="h-3.5 w-3.5 mr-1.5" /> Open</Button>
                      </a>
                    )}
                  </div>
                ))}
              </CardContent>
            </Card>
          )}
      </div>

      {/* RC 1.50 Pack Cleanup (operator-locked, 2026-02) — Documents
          tab (PackPreviewPanel) and Fit & Interview tab
          (PackFitPanel + InterviewIntelligenceSection) REMOVED.
          Pack Detail is a single overview surface. */}

      <SendViaGmailDialog
        open={sendOpen}
        onOpenChange={setSendOpen}
        pack={pack}
        onDraftCreated={() => loadReceipts()}
      />

      {/* RC 1.51 follow-up — Export Simplification (operator-locked,
          2026-02). PackCoverSheetPreviewModal retired alongside the
          dual PDF/DOCX export buttons. The pack screen itself is
          the authoritative composition surface — the helper line
          under the primary export button enumerates the ZIP
          contents deterministically (DOCX for CV + cover letter,
          PDF for governance artefacts). */}

      {/* RC 1.41 Phase B — LinkCoverLetterDialog removed. Cover
          letter assignment happens inside the wizard before pack
          creation; post-create packs are immutable. */}
    </div>
  );
}
